/**
 * ApprovalCard — the approval gate in the Activity View (SPEC §5, CONTRACT §10.4).
 *
 * Shows the target system, the field-by-field preview, lines with amounts and the total, companion
 * drafts submitted with it, evidence chips, the model spend behind it (approval.spend, only when the
 * approval carries one), cost, why approval is needed, and the read-back
 * (collapsed). Approve / Edit / Reject; reject asks for a reason inline. The Approve outline is
 * this surface's one --decide element. While a voice confirmation runs it shows the countdown.
 *
 * Escape inside the card stops in the capture phase (and the card carries data-esc-guard, which the
 * workspace's window listener honours), so it never closes the workspace behind it.
 */

import { useEffect, useRef, useState } from 'react';
import {
  CircleCheck, CircleX, Clock, Hand, FileText, BookOpen, Wrench, ChevronRight, TriangleAlert, Mic, Cpu,
} from 'lucide-react';
import SyntheticBadge from './SyntheticBadge.jsx';
import { formatInr, clockOf, spendText } from './agentUi.js';
import './ApprovalCard.css';

const DONE_STATUS = {
  approved: { Icon: CircleCheck, label: 'Approved, submitting', tone: 'good' },
  submitted: { Icon: CircleCheck, label: 'Submitted', tone: 'good' },
  rejected: { Icon: CircleX, label: 'Sent back', tone: 'warn' },
  expired: { Icon: Clock, label: 'Expired without a decision', tone: 'faint' },
  failed: { Icon: TriangleAlert, label: 'Submission failed', tone: 'bad' },
};

const EVIDENCE_ICON = { file: FileText, citation: BookOpen, tool_call: Wrench };

// The currency comes from the data, else the `defaultCurrency` prop (the vertical's, from Dashboard);
// with neither, the amount is shown without one.
const money = (amount, currency) => (amount === null || amount === undefined
  ? '' : `${formatInr(amount)}${currency ? ` ${currency}` : ''}`);

/** Seconds left on the voice confirmation, ticking once a second. */
function useCountdown(confirm) {
  const key = confirm ? `${confirm.state}|${confirm.expires_in_s ?? ''}|${confirm.expires_at ?? ''}` : null;
  const [left, setLeft] = useState(null);
  useEffect(() => {
    if (!key) { setLeft(null); return undefined; }
    const [, inS, at] = key.split('|');
    const end = at ? Date.parse(at) : Date.now() + (Number(inS) || 0) * 1000;
    const tick = () => setLeft(Number.isFinite(end) ? Math.max(0, Math.ceil((end - Date.now()) / 1000)) : null);
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [key]);
  return left;
}

export default function ApprovalCard({
  approval,
  draft = null,
  canApprove = false,
  voiceConfirm = null,
  roleLabel = (r) => r,
  onApprove,
  onEdit,
  onReject,
  onVisible,
  onOpenFile,
  onOpenCitation,
  readOnly = false,
  /** Currency for amounts whose data names none (the vertical's; generic default: none). */
  defaultCurrency = null,
}) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const reasonRef = useRef(null);
  const pending = approval?.status === 'pending';
  const primaryDraftId = approval?.draft_id ?? null;

  // While visible (and pending) the primary draft is what "this" and "line 2" mean.
  useEffect(() => {
    if (!pending || typeof onVisible !== 'function') return undefined;
    onVisible(primaryDraftId);
    return () => onVisible(null);
  }, [pending, primaryDraftId, onVisible]);

  useEffect(() => { if (rejecting) reasonRef.current?.focus(); }, [rejecting]);

  const vc = voiceConfirm && (!voiceConfirm.approval_id || voiceConfirm.approval_id === approval?.id)
    ? voiceConfirm
    : (approval?.voice ? { state: approval.voice.state, expires_at: approval.voice.expires_at } : null);
  const left = useCountdown(pending ? vc : null);

  if (!approval) return null;
  const preview = approval.preview || {};
  const currency = preview.currency || approval.cost?.currency || defaultCurrency;
  const required = approval.required_roles || [];
  const needs = required.length ? `Needs ${roleLabel(required[0])}` : 'Not allowed for your role';
  const done = DONE_STATUS[approval.status];
  const DoneIcon = done?.Icon;
  const external = approval.result?.external || [];
  // Model cost carries its own currency (the provider price), never the vertical default.
  const spend = spendText(approval.spend);

  const submitReject = () => {
    const r = reason.trim();
    if (!r) return;
    onReject?.(r);
    setRejecting(false);
    setReason('');
  };

  return (
    <section
      className={`apcard is-${approval.status}`}
      aria-label={`Approval: ${approval.action_label}`}
      data-esc-guard="approval"
      onKeyDownCapture={(e) => {
        if (e.key !== 'Escape') return;
        e.stopPropagation();
        if (rejecting) { setRejecting(false); setReason(''); }
      }}
    >
      <header className="apcard__head">
        <span className="apcard__icon"><Hand size={14} aria-hidden="true" /></span>
        <div className="apcard__titles">
          <div className="apcard__title">{approval.action_label}</div>
          {(approval.reasons || []).length > 0 && (
            <div className="apcard__reasons">{approval.reasons.join(' · ')}</div>
          )}
        </div>
        <span className="apcard__target" title="Target system">{approval.target_system}</span>
      </header>

      {(preview.fields || []).length > 0 && (
        <dl className="apcard__fields">
          {preview.fields.map((f) => (
            <div key={`${f.label}`} className="apcard__field">
              <dt>{f.label}</dt>
              <dd>{String(f.value ?? '')}</dd>
            </div>
          ))}
        </dl>
      )}

      {(preview.lines || []).length > 0 && (
        <table className="apcard__lines">
          <tbody>
            {preview.lines.map((l) => (
              <tr key={`${l.no}-${l.description}`}>
                <td className="apcard__no">{l.no}</td>
                <td className="apcard__desc">{l.description}</td>
                <td className="apcard__qty">{l.qty} {l.unit}</td>
                <td className="apcard__amt">{l.amount !== undefined && l.amount !== null ? formatInr(l.amount) : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {preview.total !== undefined && preview.total !== null && (
        <div className="apcard__total">
          <span>Total incl. tax</span>
          <strong>{money(preview.total, currency)}</strong>
        </div>
      )}

      {(preview.companions || []).length > 0 && (
        <ul className="apcard__companions">
          {preview.companions.map((c) => (
            <li key={c.draft_id}><ChevronRight size={11} aria-hidden="true" /> {c.label}</li>
          ))}
        </ul>
      )}

      {(approval.evidence || []).length > 0 && (
        <div className="apcard__evidence">
          {approval.evidence.map((ev) => {
            const EvIcon = EVIDENCE_ICON[ev.kind] ?? FileText;
            const clickable = (ev.kind === 'file' && onOpenFile) || (ev.kind === 'citation' && onOpenCitation);
            return (
              <button
                key={`${ev.kind}-${ev.ref}`}
                type="button"
                className="apcard__ev"
                disabled={!clickable}
                onClick={() => {
                  if (ev.kind === 'file') onOpenFile?.(ev.ref);
                  else if (ev.kind === 'citation') onOpenCitation?.({ cad_id: ev.cad_id ?? ev.ref, ref: ev.ref });
                }}
                title={ev.ref}
              >
                <EvIcon size={11} aria-hidden="true" /> {ev.label}
              </button>
            );
          })}
        </div>
      )}

      {spend && (
        <div className="apcard__spend" title="Model calls behind this approval">
          <Cpu size={11} aria-hidden="true" /> <span>Model spend: {spend}</span>
        </div>
      )}

      {approval.cost && approval.cost.amount !== preview.total && (
        <div className="apcard__cost">Cost: {money(approval.cost.amount, approval.cost.currency || defaultCurrency)}</div>
      )}

      {(approval.reopened || []).length > 0 && pending && (
        <div className="apcard__reopened">
          <TriangleAlert size={12} aria-hidden="true" />
          <span>Changed since it was approved: {approval.reopened.at(-1).reasons.join('; ')}</span>
        </div>
      )}

      {(approval.read_back || []).length > 0 && (
        <details className="apcard__readback">
          <summary>Read-back</summary>
          <ol>{approval.read_back.map((line, i) => <li key={`${i}-${line}`}>{line}</li>)}</ol>
        </details>
      )}

      {pending && vc?.state === 'awaiting_confirm' && (
        <div className="apcard__voice" role="status">
          <Mic size={12} aria-hidden="true" />
          Waiting for &lsquo;confirm&rsquo;{left !== null ? ` (${left} s)` : ''}
        </div>
      )}
      {pending && vc?.state === 'reading_back' && (
        <div className="apcard__voice" role="status"><Mic size={12} aria-hidden="true" /> Reading back…</div>
      )}

      {pending && !readOnly && !rejecting && (
        <div className="apcard__actions">
          <button
            type="button"
            className="apcard__btn apcard__btn--approve"
            disabled={!canApprove}
            title={canApprove ? 'Approve and submit' : needs}
            onClick={() => onApprove?.(approval.draft_version ?? draft?.version ?? null)}
          >
            Approve
          </button>
          <button type="button" className="apcard__btn" onClick={() => onEdit?.()} title="Open the draft for editing; the approval stays pending">
            Edit
          </button>
          <button type="button" className="apcard__btn" onClick={() => setRejecting(true)}>
            Reject
          </button>
          {!canApprove && <span className="apcard__needs">{needs}</span>}
        </div>
      )}

      {pending && !readOnly && rejecting && (
        <form className="apcard__reject" onSubmit={(e) => { e.preventDefault(); submitReject(); }}>
          <input
            ref={reasonRef}
            className="apcard__reason"
            type="text"
            value={reason}
            maxLength={300}
            placeholder="Why? (sent back to the specialist)"
            onChange={(e) => setReason(e.target.value)}
          />
          <button type="submit" className="apcard__btn" disabled={!reason.trim()}>Send back</button>
          <button type="button" className="apcard__btn" onClick={() => { setRejecting(false); setReason(''); }}>Cancel</button>
        </form>
      )}

      {done && (
        <div className={`apcard__status is-${done.tone}`}>
          <DoneIcon size={13} aria-hidden="true" />
          <span>{done.label}</span>
          {approval.status === 'rejected' && approval.reason && <span>: {approval.reason}</span>}
          {external.length > 0 && (
            <span className="apcard__ext">{external.map((x) => x.id).join(' · ')}</span>
          )}
          {approval.decided_by?.name && (
            <span className="apcard__by">
              {approval.decided_by.name}{approval.decided_by.channel ? ` by ${approval.decided_by.channel}` : ''} {clockOf(approval.decided_at)}
            </span>
          )}
        </div>
      )}

      <footer className="apcard__foot">
        <SyntheticBadge />
        {pending && approval.expires_at && <span className="apcard__exp">Expires {clockOf(approval.expires_at)}</span>}
      </footer>
    </section>
  );
}
